import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { colors, spacing, typography, motion } from './theme';
import { BrandLogo } from './components/BrandLogo';

const label = {
  fontFamily: typography.label.family,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  fontSize: 11,
  fontWeight: 600,
  color: colors.gold,
};

const display = {
  fontFamily: typography.display.family,
  fontWeight: 500,
  letterSpacing: '-0.015em',
  lineHeight: 1.08,
  color: colors.paper,
};

const container = {
  width: '100%',
  maxWidth: 880,
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingLeft: spacing.lg,
  paddingRight: spacing.lg,
  boxSizing: 'border-box',
};

const sectionHeading = {
  fontFamily: typography.display.family,
  fontWeight: 500,
  fontSize: 28,
  color: colors.paper,
  letterSpacing: '-0.01em',
  lineHeight: 1.2,
  marginTop: spacing.xl,
  marginBottom: spacing.md,
};

const bodyText = {
  fontFamily: typography.body.family,
  fontSize: 16,
  lineHeight: 1.75,
  color: colors.bone,
  margin: 0,
  marginBottom: spacing.md,
};

const listStyle = {
  fontFamily: typography.body.family,
  fontSize: 16,
  lineHeight: 1.85,
  color: colors.bone,
  paddingLeft: 22,
  marginTop: spacing.sm,
  marginBottom: spacing.md,
};

const hairline = { height: 1, width: '100%', background: colors.hairline };

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Privacy Policy — Black-Ops Luxury Car Service';
  }, []);

  return (
    <main
      data-testid="privacy-root"
      style={{
        backgroundColor: colors.void,
        color: colors.bone,
        minHeight: '100vh',
        fontFamily: typography.body.family,
      }}
    >
      {/* Top bar */}
      <header
        style={{
          borderBottom: `1px solid ${colors.hairlineSoft}`,
          paddingTop: 18,
          paddingBottom: 18,
        }}
      >
        <div
          style={{
            ...container,
            maxWidth: 1280,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: spacing.md,
          }}
        >
          <Link to="/" data-testid="privacy-nav-home" aria-label="Black-Ops home">
            <BrandLogo size={150} />
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-home"
            style={{
              ...label,
              color: colors.champagne,
              border: `1px solid ${colors.champagne}`,
              paddingTop: 11,
              paddingBottom: 11,
              paddingLeft: 22,
              paddingRight: 22,
              transition: `background ${motion.fast}ms ${motion.easeOut}, color ${motion.fast}ms ${motion.easeOut}`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = colors.champagne;
              e.currentTarget.style.color = colors.void;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = colors.champagne;
            }}
          >
            ← Return to Site
          </Link>
        </div>
      </header>

      {/* Content */}
      <article style={{ ...container, paddingTop: spacing.xxl, paddingBottom: spacing.xxxl }}>
        <div style={{ ...label, marginBottom: 14 }}>Legal · Privacy</div>
        <h1
          style={{
            ...display,
            fontSize: 'clamp(36px, 5vw, 64px)',
            margin: 0,
            marginBottom: spacing.md,
          }}
        >
          Privacy Policy
        </h1>
        <p
          style={{
            fontFamily: typography.body.family,
            color: colors.ash,
            fontSize: 16,
            lineHeight: 1.6,
            margin: 0,
            marginBottom: spacing.xl,
          }}
        >
          Black-Ops Luxury Car Service
        </p>

        <p style={bodyText}>
          At Black-Ops Luxury Car Service, we are committed to protecting your
          privacy as a visitor to our website and as our valued client. To our
          visitors and clients, we offer this pledge:
        </p>

        <h2 style={sectionHeading} data-testid="privacy-commitment">Our Commitment to Your Privacy</h2>
        <p style={bodyText}>
          Black-Ops Luxury Car Service owns the information collected on this
          website and through our booking platform. We will not sell, share,
          disclose, trade, transfer, lease, or rent any personally identifiable
          information to any third party unless specifically authorized by you
          or as disclosed in this Privacy Policy. Additionally, SMS opt-ins and
          phone numbers collected for SMS purposes will not be shared with any
          third parties or affiliate companies for marketing or promotional
          purposes.
        </p>

        <h2 style={sectionHeading} data-testid="privacy-changes">Changes to Our Privacy Policy</h2>
        <p style={bodyText}>
          We reserve the right to update or modify this Privacy Policy at any
          time without advance notice. We encourage you to review this policy
          periodically. If you have any questions about our Privacy Policy,
          please contact us via the Contact section of our website or by
          calling our concierge line directly.
        </p>

        <h2 style={sectionHeading} data-testid="privacy-sms">SMS Communication & Text Messaging</h2>
        <p style={bodyText}>
          If you provide us with a cell phone number to receive text messages,
          you are agreeing to SMS communication with Black-Ops Luxury Car
          Service. We use SMS to send booking confirmations, driver arrival
          alerts, ride receipts, appointment reminders, and service
          notifications.
        </p>
        <p style={bodyText}>
          Text messaging opt-in data and consent will not be shared with any
          third parties. Messaging and data rates may apply. By providing your
          number, you consent to receive SMS messages from Black-Ops Luxury Car
          Service. You understand that message and data rates may apply and
          that you may reply <strong style={{ color: colors.champagne }}>STOP</strong> to opt
          out of future messages or reply <strong style={{ color: colors.champagne }}>HELP</strong> for
          additional assistance. Message frequency may vary based on your
          bookings and interactions with our team.
        </p>
        <p style={{ ...bodyText, fontWeight: 600, color: colors.paper }} data-testid="privacy-sms-no-share">
          Mobile opt-in, SMS consent, and phone numbers will not be shared
          with third parties or affiliates for marketing purposes.
        </p>

        <h2 style={sectionHeading} data-testid="privacy-information">Information We Collect</h2>
        <p style={bodyText}>
          We will ask you to provide personal information when you create an
          account, make a booking, or contact us directly. This may include
          your:
        </p>
        <ul style={listStyle}>
          <li>Full name</li>
          <li>Home or business address</li>
          <li>Pickup and drop-off locations</li>
          <li>Telephone number and email address</li>
          <li>Corporate account or billing information</li>
          <li>Flight details for airport transfer coordination</li>
        </ul>
        <p style={bodyText}>
          This information is used solely to fulfill your transportation
          reservations, communicate with you about your rides, and maintain
          your client profile for future bookings. All staff members of
          Black-Ops Luxury Car Service are required to maintain strict
          confidentiality regarding client information and have been instructed
          on the importance of protecting your personal data.
        </p>

        <h2 style={sectionHeading} data-testid="privacy-payment">Payment Information</h2>
        <p style={bodyText}>
          When you book a ride with Black-Ops Luxury Car Service, payment is
          processed securely through our platform powered by Stripe. If you pay
          by credit or debit card, your card information is encrypted and
          processed through Stripe&rsquo;s PCI-compliant infrastructure.
          Black-Ops Luxury Car Service does not store your full card number on
          our servers. A transaction confirmation will be provided to you via
          email or SMS following each completed payment. Saved payment methods
          are stored securely within your client profile and may be used for
          post-ride automatic charges in accordance with your authorization at
          the time of booking.
        </p>

        <h2 style={sectionHeading} data-testid="privacy-cookies">Cookies</h2>
        <p style={bodyText}>
          Our website may use cookies to enhance your browsing and booking
          experience. A cookie is a small piece of data stored on your device
          while you visit our site. We use cookies solely to distinguish unique
          visitors and to carry forward information you enter during the
          booking process, improving your experience. No personally
          identifiable information is stored within the cookie itself. Cookies
          expire when you close your browser or after a period of inactivity.
          Returning visitors will be issued a new cookie and will not be
          identified from any prior session.
        </p>

        <h2 style={sectionHeading} data-testid="privacy-third-party">Third-Party Links</h2>
        <p style={bodyText}>
          Our website may contain links to third-party websites, including
          hotel partners, event venues, or travel platforms, that are not
          controlled by Black-Ops Luxury Car Service. These links are provided
          for your convenience. Black-Ops Luxury Car Service is not responsible
          for the content or privacy practices of any third-party websites.
          Our Privacy Policy applies only to information collected while you
          are actively engaged with our website or booking platform. We
          encourage you to review the privacy policies of any third-party
          sites you visit.
        </p>

        <h2 style={sectionHeading} data-testid="privacy-ada">ADA Compliance</h2>
        <p style={bodyText}>
          Black-Ops Luxury Car Service is committed to maintaining a website
          and digital booking experience that is accessible to all users,
          including those utilizing assistive technologies. We strive to align
          with the Web Content Accessibility Guidelines (WCAG) established by
          the World Wide Web Consortium (W3C) and conduct ongoing reviews to
          identify and address accessibility improvements.
        </p>

        <h2 style={sectionHeading} data-testid="privacy-contact">Contact Us</h2>
        <p style={bodyText}>
          For questions, concerns, or requests related to your personal data
          or this Privacy Policy, please contact us:
        </p>

        <div
          data-testid="privacy-contact-card"
          style={{
            background: colors.onyx,
            border: `1px solid ${colors.steel}`,
            padding: spacing.xl,
            marginTop: spacing.md,
            marginBottom: spacing.lg,
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.md,
          }}
        >
          <div
            style={{
              fontFamily: typography.display.family,
              fontWeight: 500,
              fontSize: 22,
              color: colors.paper,
              letterSpacing: '-0.01em',
            }}
          >
            Black-Ops Luxury Car Service
          </div>
          <div
            style={{
              fontFamily: typography.body.family,
              color: colors.ash,
              fontSize: 15,
              lineHeight: 1.6,
            }}
          >
            Phoenix, Arizona&nbsp;&nbsp;|&nbsp;&nbsp;Serving Greater Maricopa County
          </div>

          <div style={hairline} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
            <div style={{ display: 'flex', gap: spacing.md, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ ...label, color: colors.gold, minWidth: 80 }}>Phone</span>
              <a
                href="tel:+18442556771"
                data-testid="privacy-phone"
                style={{ fontFamily: typography.body.family, color: colors.paper, fontSize: 17 }}
              >
                844-BLK-OPS1
              </a>
            </div>
            <div style={{ display: 'flex', gap: spacing.md, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ ...label, color: colors.gold, minWidth: 80 }}>Email</span>
              <a
                href="mailto:pickup@blk-ops1.com"
                data-testid="privacy-email"
                style={{ fontFamily: typography.body.family, color: colors.paper, fontSize: 17 }}
              >
                pickup@blk-ops1.com
              </a>
            </div>
            <div style={{ display: 'flex', gap: spacing.md, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ ...label, color: colors.gold, minWidth: 80 }}>Website</span>
              <a
                href="https://www.black-opslcs.com"
                data-testid="privacy-website"
                style={{ fontFamily: typography.body.family, color: colors.paper, fontSize: 17 }}
              >
                www.black-opslcs.com
              </a>
            </div>
          </div>
        </div>

        <div style={{ ...hairline, marginTop: spacing.xl, marginBottom: spacing.lg }} />

        <div
          style={{
            ...label,
            color: colors.ash,
            letterSpacing: '0.3em',
            fontSize: 10,
          }}
        >
          Last updated · {new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' })}
        </div>
      </article>

      {/* Footer */}
      <footer
        style={{
          borderTop: `1px solid ${colors.hairlineSoft}`,
          paddingTop: spacing.xl,
          paddingBottom: spacing.xl,
        }}
      >
        <div
          style={{
            ...container,
            maxWidth: 1280,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: spacing.md,
            ...label,
            color: colors.ash,
            fontSize: 10,
          }}
        >
          <span>© {new Date().getFullYear()} Black-Ops Luxury Car Service, LLC.</span>
          <Link to="/" data-testid="privacy-footer-home" style={{ color: colors.bone }}>
            Return to Site
          </Link>
          <span>www.black-opslcs.com</span>
        </div>
      </footer>
    </main>
  );
}
